/**
 * Filters for @jupyterlab/services teardown-related errors.
 *
 * When a kernel connection is disposed (restart, watchdog teardown,
 * workspace switch, page unload), the library cancels all pending
 * futures -- including internal kernel_info_request messages that are
 * not directly awaited by application code. The resulting unhandled
 * rejections are expected and not application bugs.
 *
 * The library also rejects any pending kernel.reconnect() promise with
 * "Kernel connection disconnected" when the kernel is disposed mid-
 * reconnect (graceful restart timeout fallback, kernel auto-restart
 * that ultimately fails, sim/workspace teardown returning 401/503 for
 * the kernel API). Same teardown shape, different message.
 */

const CANCELED_FUTURE_SUBSTR = 'Canceled future';
const KERNEL_DISCONNECTED_SUBSTR = 'Kernel connection disconnected';

/**
 * Check whether an unhandled rejection reason is a Jupyter canceled
 * future error that should be silently suppressed.
 */
export function isJupyterCanceledFuture(reason: unknown): boolean {
  return reason instanceof Error && reason.message.includes(CANCELED_FUTURE_SUBSTR);
}

/**
 * Check whether an unhandled rejection reason is a "Kernel connection
 * disconnected" error from @jupyterlab/services. This is thrown when
 * a pending kernel.reconnect() promise is rejected because the kernel
 * was disposed before reconnection completed.
 */
export function isKernelConnectionDisconnected(reason: unknown): boolean {
  return reason instanceof Error && reason.message.includes(KERNEL_DISCONNECTED_SUBSTR);
}

/**
 * Check whether an unhandled rejection reason is any expected
 * @jupyterlab/services teardown rejection (canceled future or
 * kernel connection disconnected).
 */
export function isJupyterTeardownRejection(reason: unknown): boolean {
  return isJupyterCanceledFuture(reason) || isKernelConnectionDisconnected(reason);
}
